<template>
  <transition name="fade">
    <div v-if="visible">
      <div v-if="type === 'default'" class="toastDefault">
        <span>{{ text }}</span>
      </div>
      <div v-else-if="text || linkText" class="toast" :class="[type, { hideIcon, hasLink: !!linkText }]">
        <div>
          <img v-if="!hideIcon && imgType[type]" :src="imgType[type]" />
          <span v-if="linkText"
            ><span class="linkTextPrefix">{{ text }}</span
            ><span class="link" @click.stop="handleLinkClick">{{ linkText }}</span></span
          >
          <span v-else>{{ text }}</span>
        </div>
      </div>
      <div v-if="code" class="toastCode">{{ code }}</div>
      <div v-if="lock" class="lock" />
      <div v-if="showMask" class="mask" />
    </div>
  </transition>
</template>

<script setup>
import { ref } from 'vue';

// 图标
import iconSuccess from '@/assets/status-icons/success.png'; // 成功图标
import iconFail from '@/assets/status-icons/error.png'; // 失败图标
import iconWarning from '@/assets/status-icons/warn-black.png'; // 警告图标

const visible = ref(false); // 是否显示
const type = ref('warning'); // 类型
const text = ref(''); // 文本
const linkText = ref(''); // 可点击链接文案
const onLinkClick = ref(null); // 链接点击回调
const code = ref(''); // 错误码（右下角单独展示）
const lock = ref(false); // 是否锁定
const hideIcon = ref(false); // 是否隐藏图标
const showMask = ref(false); // 是否显示遮罩
const imgType = {
  success: iconSuccess, // 成功图标
  error: iconFail, // 失败图标
  warning: iconWarning, // 警告图标
}; // 图片类型
let timer = null; // 定时器

const hideToast = () => {
  visible.value = false;
  code.value = '';
  linkText.value = '';
  onLinkClick.value = null;
};

const handleLinkClick = () => {
  if (typeof onLinkClick.value === 'function') {
    onLinkClick.value();
  }
  clearTimeout(timer);
  hideToast();
};

/** 文案末尾 【code】 拆开展示：正文保留，code 右下角 */
const splitToastTextAndCode = rawText => {
  const str = String(rawText || '');
  const match = str.match(/【\s*([^】]+?)\s*】\s*$/);
  if (!match) {
    return { text: str, code: '' };
  }
  return {
    text: str.slice(0, match.index).trimEnd(),
    code: match[1].trim(),
  };
};

/**
 * 显示 toast
 */
const showToast = options => {
  type.value = options.type;
  const split = splitToastTextAndCode(options.text || '');
  text.value = split.text;
  linkText.value = options.linkText || '';
  onLinkClick.value = options.onLinkClick || null;
  code.value = split.code || (options.code != null && options.code !== '' ? String(options.code) : '');
  lock.value = options.lock || false;
  hideIcon.value = options.hideIcon || false;
  showMask.value = options.showMask || false;

  if (text.value == '' && !code.value && !linkText.value) {
    return;
  }

  if (options.type == 'hide') {
    hideToast();
    return;
  }
  visible.value = true;
  clearTimeout(timer);

  // 如果 duration 不为 -1，则设置定时器隐藏 toast
  if (options.duration !== -1) {
    timer = setTimeout(() => {
      hideToast();
    }, options.duration || 1500);
  }
};

defineExpose({ showToast });
</script>

<style scoped lang="less">
.toastDefault {
  position: fixed;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: block;
  background: rgba(0, 0, 0, 0.8);
  height: 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding: 0 0.15rem;
  font-size: 0.16rem;
  font-weight: 400;
  z-index: 9999;
  white-space: nowrap;
  border-radius: 0.06rem;
}

.mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 9998;
}

.lock {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
}

.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3.6rem;
  display: block;
  font-size: 0.16rem;
  font-weight: 400;
  z-index: 9999;
  text-align: center;
  &.hideIcon {
    > div {
      padding-left: 0.16rem;
    }
  }

  > div {
    display: inline-block;
    line-height: 0.24rem;
    padding: 0.1rem 0.16rem 0.1rem 0.45rem;
    border-radius: 0.08rem;
    box-shadow: 0 0.02rem 0.03rem 0 rgba(0, 0, 0, 0.13);
    position: relative;
    text-align: left;
    max-width: 100%;

    img {
      width: 0.21rem;
      height: 0.21rem;
      position: absolute;
      left: 0.16rem;
      top: 50%;
      transform: translateY(-50%);
    }
    span {
      white-space: normal;
      word-break: break-all;
    }
  }

  &.hasLink {
    > div {
      .linkTextPrefix {
        color: #000;
      }

      .link {
        margin-left: 0.04rem;
        font-weight: 500;
        cursor: pointer;
      }
    }

    &.error > div .link {
      color: #d02b2b;
    }
  }

  &.success {
    > div {
      background-color: #edfeee;
      color: #1da949;
      border: 0.005rem solid #b3e6c0;
    }
  }
  &.error {
    > div {
      background-color: #fff0f1;
      color: #d02b2b;
      border: 0.005rem solid #f2b9ba;
    }
  }
  &.warning {
    > div {
      background-color: #eeeeee;
      color: #2a2a2a;
      border: 0.005rem solid #dddddd;
    }
  }
}

.toastCode {
  position: fixed;
  right: 0.2rem;
  bottom: 0.23rem;
  height: 0.25rem;
  line-height: 0.25rem;
  padding: 0 0.12rem;
  font-size: 0.14rem;
  color: #d02b2b;
  border: 0.01rem solid #f2b9ba;
  border-radius: 0.06rem;
  background: #fff0f1;
  z-index: 9999;
}
</style>
